// ─────────────────────────────────────────────────────────────────────────────
// components.tsx — Shared primitives for ContentSectionEditPageProduct
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { Toggle } from '@core/uilibrary';
import TextFieldInline from '../../../../../../component-controller-mod/component-serv/input-fs-cp/text-field-op/ts/textFieldInline';
import TextAreaInline from '../../../../../../component-controller-mod/component-serv/input-fs-cp/textarea-field-op/ts/textAreaInline';
import ContentFileUpload from '../../../../../../component-controller-mod/component-serv/card-fs-cp/upload-card-op/ts/contentFileUpload';

// ─────────────────────────────────────────────────────────────────────────────
// DraftTextField
// ─────────────────────────────────────────────────────────────────────────────

export const DraftTextField: React.FC<{
  initialValue?: string;
  placeholder?:  string;
  onChange:      (v: string) => void;
}> = ({ initialValue = '', placeholder, onChange }) => {
  const [draft, setDraft] = useState(initialValue);
  return (
    <TextFieldInline
      value={draft}
      placeholder={placeholder}
      onChange={(v: string) => { setDraft(v); onChange(v); }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DraftTextArea
// ─────────────────────────────────────────────────────────────────────────────

export const DraftTextArea: React.FC<{
  initialValue?: string;
  placeholder?:  string;
  rows?:         number;
  onChange:      (v: string) => void;
}> = ({ initialValue = '', placeholder, rows, onChange }) => {
  const [draft, setDraft] = useState(initialValue);
  return (
    <TextAreaInline
      value={draft}
      placeholder={placeholder}
      rows={rows}
      onChange={(v: string) => { setDraft(v); onChange(v); }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LabelRow
// ─────────────────────────────────────────────────────────────────────────────

export const LabelRow: React.FC<{
  label:     string;
  required?: boolean;
  toggle?:   { checked: boolean; onChange: (v: boolean) => void };
}> = ({ label, required, toggle }) => (
  <div className="scl--cms-content-edit-product-label-row">
    <span className="scl--cms-content-edit-product-label">
      {label}{required && <span className="scl--cms-content-edit-product-required"> *</span>}
    </span>
    {toggle && <Toggle checked={toggle.checked} onChange={toggle.onChange} />}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SplitRow
// ─────────────────────────────────────────────────────────────────────────────

export const SplitRow: React.FC<{
  currentText: string;
  multiline?:  boolean;
  children:    React.ReactNode;
}> = ({ currentText, multiline, children }) => (
  <div className={`scl--cms-content-edit-product-split-row${multiline ? ' scl--cms-content-edit-product-split-row--multiline' : ''}`}>
    <div className="scl--cms-content-edit-product-split-accent" />
    <div className="scl--cms-content-edit-product-split-body">
      <span className="scl--cms-content-edit-product-split-current">{currentText}</span>
      <div className={`scl--cms-content-edit-product-split-slot${multiline ? ' scl--cms-content-edit-product-split-slot--textarea' : ''}`}>
        {children}
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ImageUploadField
// ─────────────────────────────────────────────────────────────────────────────

export const ImageUploadField: React.FC<{
  label:         string;
  currentSrc?:   string;
  currentName?:  string;
  onFile:        (f: File) => Promise<string | undefined>;
  onMediaClick?: () => void;
}> = ({ label, currentSrc, currentName, onFile, onMediaClick }) => {
  const [previewSrc,  setPreviewSrc]  = useState<string | undefined>(currentSrc);
  const [previewName, setPreviewName] = useState<string | undefined>(currentName);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (currentSrc?.startsWith('blob:')) return;
    setPreviewSrc(currentSrc);

    if (currentName) {
      setPreviewName(currentName);
    } else if (currentSrc) {
      try {
        const url  = new URL(currentSrc);
        const name = url.pathname.split('/').pop() ?? currentSrc;
        setPreviewName(name);
      } catch {
        const name = currentSrc.split('/').pop()?.split('?')[0] ?? currentSrc;
        setPreviewName(name);
      }
    } else {
      setPreviewName(undefined);
    }
  }, [currentSrc, currentName]);

  const handleFilesSelected = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const uploadedUrl = await onFile(file);
      if (uploadedUrl) {
        setPreviewSrc(uploadedUrl);
        setPreviewName(file.name);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="scl--cms-content-edit-product-upload-block">
      <LabelRow label={label} />
      <div className="scl--cms-content-edit-product-upload-row">
        <div className="scl--cms-content-edit-product-upload-preview">
          <div className="scl--cms-content-edit-product-upload-preview-inner">
            <div className="scl--cms-content-edit-product-upload-thumb">
              {isUploading ? (
                <span className="scl--cms-content-edit-product-upload-loading">Uploading…</span>
              ) : previewSrc && (
                <img
                  key={previewSrc}
                  src={previewSrc}
                  alt={previewName}
                  onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              )}
            </div>
            <div className="scl--cms-content-edit-product-upload-meta">
              <span className="scl--cms-content-edit-product-upload-name">
                {isUploading ? 'Uploading…' : (previewName ?? 'No file selected')}
              </span>
            </div>
          </div>
        </div>
        <div className="scl--cms-content-edit-product-upload-zone-wrap">
          <ContentFileUpload
            accept=".jpg,.jpeg,.png,.svg"
            supportText="JPG, PNG or SVG"
            onFilesSelected={handleFilesSelected}
            onMediaClick={onMediaClick}
          />
        </div>
      </div>
    </div>
  );
};